import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  Check,
  Clock3,
  FolderKanban,
  ReceiptText,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import BoardWorkspace from "@/components/BoardWorkspace";
import QuickFollowUpDialog from "@/components/QuickFollowUpDialog";
import { Link } from "react-router-dom";
import { formatBusinessDate, formatTimestamp } from "@/utils/datetime";
import GlobalSearch from "@/components/GlobalSearch";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const exceptionLabels = {
  overdue_follow_up: "Follow-up",
  permit_locate: "Permit / Locate",
  stalled_invoice: "Invoice",
};

export default function ExceptionDesk() {
  const { canAccessBilling, user } = useAuth();
  const canViewInformation = ["owner_admin", "office_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [queueError, setQueueError] = useState("");
  const loadRequest = useRef(0);
  const openCommunications = useMemo(() => {
    return (data?.communications || []).filter(
      (item) => item.status === "open" && item.item_type === "note",
    );
  }, [data?.communications]);

  const loadDesk = useCallback(async () => {
    const request = ++loadRequest.current;
    setLoading(true);
    setError("");
    const [bootstrap, queue] = await Promise.allSettled([
      axios.get(`${API}/bootstrap`),
      axios.get(`${API}/action-queue`, { params: { filter: "open" } }),
    ]);
    if (request !== loadRequest.current) return;
    if (bootstrap.status === "rejected") {
      setError("Could not load the operations workspace. Check your connection and try again.");
    } else {
      setQueueError(queue.status === "rejected" ? "Action Queue could not be loaded. Its current work is unavailable." : "");
      setData({ ...bootstrap.value.data,
        action_queue: queue.status === "fulfilled" ? queue.value.data.items?.slice(0, 5) || [] : [],
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadDesk();
    const requests = loadRequest;
    return () => { requests.current++; };
  }, [loadDesk]);

  const completeTask = async (item) => {
    try {
      await axios.post(`${API}/communication-items/${item.id}/complete`);
      toast.success("Follow-up marked complete.");
      loadDesk();
    } catch {
      toast.error("Could not complete this follow-up.");
    }
  };

  return (
    <BoardWorkspace
      actions={
        <div className="exception-desk-actions" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {canViewInformation && (
            <Link className="table-open-link" data-testid="nav-information-link" to="/information">
              Information
            </Link>
          )}
          <GlobalSearch />
        </div>
      }
      eyebrow="Operations overview"
      headingTestId="exception-desk-heading"
      subtitle="Current priorities, action-queue follow-through, and recent operating activity."
      title="Business Pulse"
    >
      {error && <section className="board-error-state" role="alert">
        <p>{error}</p><button type="button" onClick={loadDesk} disabled={loading}>Try again</button>
      </section>}
      {!data && !error && <p role="status" data-testid="operations-loading">Loading the operations desk…</p>}
      {data && <div data-testid="exception-desk">
        <section className="ops-metrics" data-testid="exception-metrics">
          <Metric
            description="Informational count of Projects currently in progress."
            icon={FolderKanban}
            label="Active jobs"
            value={data.metrics.active_jobs}
            tone="blue"
          />
          <Metric
            icon={Clock3}
            label="Overdue follow-ups"
            value={data.metrics.overdue_follow_ups}
            tone="red"
          />
          {canAccessBilling && (
            <Metric
              icon={ReceiptText}
              label="Stalled invoices"
              value={data.metrics.stalled_invoices}
              tone="orange"
            />
          )}
        </section>
        <section className="exception-layout">
          <div className="exception-column">
            <div className="section-head">
              <div>
                <span className="ops-eyebrow">ACTION QUEUE</span>
                <h2>Open work needing attention</h2>
              </div>
              <Link className="table-open-link" data-testid="dashboard-open-action-queue" to="/action-queue">
                Open Action Queue
              </Link>
              <span data-testid="exception-count" className="count-chip">
                {data.action_queue.length}
              </span>
            </div>
            {queueError && <div role="alert"><p>{queueError}</p>
              <button type="button" onClick={loadDesk} disabled={loading}>Retry Action Queue</button>
            </div>}
            <div className="exception-list">
              {data.action_queue.map((item, index) => (
                <Link
                  className="exception-item priority-medium"
                  data-testid={`dashboard-action-queue-item-${item.id}`}
                  key={item.id}
                  to={item.project_id ? `/projects/${item.project_id}` : "/action-queue"}
                >
                  <div className="exception-icon">
                    <AlertTriangle size={16} />
                  </div>
                  <div className="exception-copy">
                    <div className="exception-meta">
                      <span>{item.item_type?.replaceAll("_", " ")}</span>
                      <span>{item.project_name || "Company-wide"}</span>
                    </div>
                    <h3>{item.title}</h3>
                    <p>Due {item.due_at || item.due_date ? formatTimestamp(item.due_at || item.due_date) : "now"}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <aside className="followup-column">
            <div className="section-head">
              <div>
                <span className="ops-eyebrow">COMMUNICATION LOG</span>
                <h2>Open follow-ups</h2>
              </div>
              <QuickFollowUpDialog
                onCreated={loadDesk}
                projects={data.projects}
              />
            </div>
            <div className="followup-list">
              {openCommunications.map((item) => (
                  <article
                    className="followup-item"
                    data-testid={`followup-${item.id}`}
                    key={item.id}
                  >
                    <div>
                      <span className={`priority-chip ${item.priority}`}>
                        {item.priority}
                      </span>
                      <h3>{item.title}</h3>
                      <p>
                        {item.assignee} · due {formatBusinessDate(item.due_at)}
                      </p>
                    </div>
                    <button
                      aria-label={`Complete ${item.title}`}
                      data-testid={`complete-followup-${item.id}`}
                      onClick={() => completeTask(item)}
                      title="Mark complete"
                      type="button"
                    >
                      <Check size={16} />
                    </button>
                  </article>
                ))}
            </div>
          </aside>
        </section>
        <section className="owner-lower">
          <div>
            <div className="section-head">
              <div>
                <span className="ops-eyebrow">TODAY’S TOP PRIORITIES</span>
                <h2>
                  <Sparkles size={17} /> Owner attention
                </h2>
              </div>
            </div>
            <div className="priority-list">
              {[...(data.priorities || [])].sort((first, second) => (
                String(second.priority_set_at || "").localeCompare(
                  String(first.priority_set_at || ""),
                )
              )).map((item, index) => (
                <Link
                  data-testid={`priority-item-${index}`}
                  key={`${item.kind}-${item.project_id || "unlinked"}-${index}`}
                  to={`/projects/${item.project_id}`}
                >
                  <span>{index + 1}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {item.project_name} · {item.detail}
                    </small>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>}

    </BoardWorkspace>
  );
}

function Metric({ description = "", icon: Icon, label, value, tone }) {
  return (
    <article
      className={`metric-card ${tone}`}
      data-testid={`metric-${label.toLowerCase().replaceAll(" ", "-")}`}
    >
      <Icon size={18} />
      <strong>{value}</strong>
      <span>{label}</span>
      {description && (
        <small data-testid="active-jobs-information-note">{description}</small>
      )}
    </article>
  );
}

